import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  TransferWithinAStation as TransferIcon,
  Close as CloseIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Warehouse, Product } from '../../../enums/product';
import { WarehouseService } from '../branch/branchService';

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (toWarehouseId: string, toWarehouseName: string) => Promise<void>;
  car: Product | null;
  sourceWarehouse: Warehouse;
}

// A car is a single physical unit at exactly one branch — "transferring" it
// is just reassigning which branch it belongs to, not moving a stock quantity.
const TransferDialog: React.FC<TransferDialogProps> = ({
  open,
  onClose,
  onSubmit,
  car,
  sourceWarehouse,
}) => {
  const [toWarehouseId, setToWarehouseId] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && car) {
      loadWarehouses();
      setError('');
    }
  }, [open, car]);

  const loadWarehouses = async () => {
    try {
      const activeWarehouses = await WarehouseService.getActiveWarehouses();
      const otherWarehouses = activeWarehouses.filter((w) => w.id !== sourceWarehouse.id);
      setWarehouses(otherWarehouses);
      setToWarehouseId(otherWarehouses.length > 0 ? otherWarehouses[0].id! : '');
    } catch (err) {
      console.error('Error loading show rooms:', err);
      setError('Failed to load show rooms');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const destination = warehouses.find((w) => w.id === toWarehouseId);
    if (!destination) {
      setError('Please select a destination show room');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await onSubmit(destination.id!, destination.name);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to move vehicle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TransferIcon color="primary" />
            <Typography variant="h6">Move Vehicle to Another Branch</Typography>
          </Stack>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
        >
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0 }}>
            {car && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Vehicle
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {car.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  VIN: {car.sku}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth sx={{ mb: 2 }} disabled={warehouses.length === 0}>
              <InputLabel>Destination Show Room</InputLabel>
              <Select
                value={toWarehouseId}
                onChange={(e) => setToWarehouseId(e.target.value)}
                label="Destination Show Room"
              >
                {warehouses.map((warehouse) => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name} ({warehouse.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {warehouses.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No other active show rooms available to move this vehicle to.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={onClose} disabled={loading} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!toWarehouseId || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <TransferIcon />}
            >
              {loading ? 'Moving...' : 'Move Vehicle'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default TransferDialog;
